import React, { useState } from "react";
import {
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  FileImageOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Button, Modal, Spin, Tag, Tooltip } from "antd";
import { formatFileSize, formatDate } from "../../Utils/helpers";

const DocumentCard = ({ document, onDelete, onDownload }) => {
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const getFileIcon = (fileType) => {
    const iconMap = {
      pdf: <FilePdfOutlined className="text-red-500 text-4xl" />,
      doc: <FileWordOutlined className="text-blue-500 text-4xl" />,
      docx: <FileWordOutlined className="text-blue-500 text-4xl" />,
      excel: <FileExcelOutlined className="text-green-500 text-4xl" />,
      xls: <FileExcelOutlined className="text-green-500 text-4xl" />,
      xlsx: <FileExcelOutlined className="text-green-500 text-4xl" />,
      csv: <FileExcelOutlined className="text-green-500 text-4xl" />,
      image: <FileImageOutlined className="text-purple-500 text-4xl" />,
    };
    return (
      iconMap[fileType] || (
        <FileTextOutlined className="text-gray-500 text-3xl" />
      )
    );
  };

  const getFileColor = (fileType) => {
    const colorMap = {
      pdf: "border-red-200 hover:border-red-400",
      doc: "border-blue-200 hover:border-blue-400",
      docx: "border-blue-200 hover:border-blue-400",
      excel: "border-green-200 hover:border-green-400",
      xls: "border-green-200 hover:border-green-400",
      xlsx: "border-green-200 hover:border-green-400",
      csv: "border-green-200 hover:border-green-400",
      image: "border-purple-200 hover:border-purple-400",
    };
    return colorMap[fileType] || "border-gray-200 hover:border-gray-400";
  };

  const handleDelete = async () => {
    setLoading(true);
    await onDelete(document._id);
    setLoading(false);
    setShowDeleteModal(false);
  };

  return (
    <>
      <div
        className={`
          relative overflow-hidden
          bg-white/90 backdrop-blur-sm
          rounded-2xl
          border border-slate-200
          shadow-sm
          hover:shadow-xl
          hover:-translate-y-1
          transition-all duration-300
          p-5
          group
        `}
      >
        <div className="flex justify-between items-start gap-4">
          <div className="flex gap-4 flex-1 min-w-0">
            <div
              className="
                flex items-center justify-center
                h-14 w-14
                rounded-xl
                bg-gradient-to-br
                from-slate-50
                to-slate-100
                shadow-sm
                group-hover:scale-105
                transition-transform
              "
            >
              {getFileIcon(document.fileType)}
            </div>

            <div className="flex-1 min-w-0">
              <Tooltip title={document.originalName}>
                <h3 className="font-semibold text-slate-800 truncate text-base">
                  {document.originalName}
                </h3>
              </Tooltip>

              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                <span>{formatFileSize(document.fileSize)}</span>
                <span>•</span>
                <span>{formatDate(document.created)}</span>
              </div>

              {document.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {document.tags.slice(0, 3).map((tag, index) => (
                    <Tag
                      key={index}
                      className="
                        rounded-full
                        border-0
                        px-2
                        bg-blue-50
                        text-blue-600
                        text-xs
                      "
                    >
                      {tag}
                    </Tag>
                  ))}

                  {document.tags.length > 3 && (
                    <Tag
                      className="
                        rounded-full
                        border-0
                        bg-slate-100
                        text-slate-600
                      "
                    >
                      +{document.tags.length - 3}
                    </Tag>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Tooltip title="Download">
              <Button
                shape="circle"
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => onDownload(document._id)}
                className="
                  hover:bg-green-50
                  hover:text-green-600
                "
              />
            </Tooltip>

            <Tooltip title="Delete">
              <Button
                shape="circle"
                type="text"
                icon={<DeleteOutlined />}
                loading={loading}
                onClick={() => setShowDeleteModal(true)}
                className="
                  hover:bg-red-50
                  hover:text-red-600
                "
              />
            </Tooltip>
          </div>
        </div>

        {document.description && (
          <div className="mt-4">
            <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
              {document.description}
            </p>
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-slate-100">
          <span className="text-xs text-slate-500">
            Uploaded by{" "}
            <span className="font-medium text-slate-700">
              {document.uploadedBy?.name || "Unknown"}
            </span>
          </span>
        </div>
      </div>

      <Modal
        title={
          <span className="text-lg font-semibold text-red-600">
            Delete Document
          </span>
        }
        open={showDeleteModal}
        onOk={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          danger: true,
          loading,
          size: "large",
        }}
        centered
      >
        <div className="py-2">
          <p className="text-slate-700">
            Are you sure you want to permanently delete
          </p>

          <p className="font-semibold mt-2 text-slate-900">
            {document.originalName}
          </p>

          <p className="text-sm text-slate-500 mt-3">
            This action cannot be undone.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default DocumentCard;